import Head from 'next/head'
import Link from 'next/link'
import { getArticleDateTime } from '../../../utils/article-date-time'
import { ArticleByline } from '../../../components/commons/ArticleByline'
import { harveyFinn } from 'data/authors'

export async function getStaticProps() {
    const { modifiedDate, year } = getArticleDateTime()
    return {
        props: { modifiedDate, year },
        revalidate: 259200,
    }
}

export default function ChoosingAPersonalTrainer({ modifiedDate, year }) {
    const publishedDate = "2026-09-05T07:30:46+00:00"
    const pageTitle = `How to Choose a Personal Trainer in Singapore [${year}]`
    const pageDescription = `How to vet a personal trainer in Singapore: certifications to check, red flags to avoid, how not to overpay, and the signs it is time to switch trainers.`
    const canonical = "https://www.stringssg.com/sg/personal-trainer/choosing-a-personal-trainer-singapore"

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta charSet="utf-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content="how to choose a personal trainer singapore, personal trainer certification singapore, switch personal trainer, personal trainer red flags" />
                <meta name="author" content={harveyFinn.name} />
                <meta httpEquiv="content-language" content="en-sg" />
                <meta property="og:locale" content="en_SG" />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="og:url" content={canonical} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="article:author" content={harveyFinn.name} />
                <meta property="article:published_time" content={publishedDate} />
                <meta property="article:modified_time" content={modifiedDate} />
                <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@stringssg" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg" />
                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href={canonical} />
                <script type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@graph": [
                                {
                                    "@type": "Article",
                                    "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
                                    "headline": pageTitle,
                                    "description": pageDescription,
                                    "image": ["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/personal-trainer.jpg"],
                                    "author": {
                                        "@type": "Person",
                                        "name": harveyFinn.name,
                                        "email": harveyFinn.email,
                                        "jobTitle": harveyFinn.jobTitle,
                                        "knowsAbout": harveyFinn.knowsAbout,
                                        "worksFor": { "@type": "Organization", "@id": "https://www.stringssg.com/#organization", "name": "StringsSG", "url": "https://www.stringssg.com" }
                                    },
                                    "publisher": {
                                        "@type": "Organization",
                                        "name": "StringsSG",
                                        "url": "https://www.stringssg.com",
                                        "logo": { "@type": "ImageObject", "url": "https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png" }
                                    },
                                    "about": { "@type": "Thing", "name": "Choosing a personal trainer in Singapore" },
                                    "datePublished": publishedDate,
                                    "dateModified": modifiedDate
                                },
                                {
                                    "@type": "BreadcrumbList",
                                    "itemListElement": [
                                        { "@type": "ListItem", "position": 1, "name": "Personal Trainer", "item": "https://www.stringssg.com/sg/personal-trainer" },
                                        { "@type": "ListItem", "position": 2, "name": "Choosing a Trainer" }
                                    ]
                                }
                            ]
                        })
                    }}
                />
            </Head>

            <div className="bg-slate-900 min-h-screen">
                <div className="max-w-5xl mx-auto px-4 py-10 text-white/70 space-y-6">
                    <div className="flex items-center gap-2 text-white/40 text-sm">
                        <Link href="/sg/personal-trainer" className="hover:text-white/60 transition-colors">Personal Trainer</Link>
                        <span>/</span>
                        <span className="text-white/60">Choosing a Trainer</span>
                    </div>

                    <h1 className="text-2xl md:text-4xl text-white font-extrabold">
                        How to Choose a Personal Trainer in Singapore
                    </h1>

                    <ArticleByline
                        author={harveyFinn}
                        publishedDate={publishedDate}
                        modifiedDate={modifiedDate}
                        variant="header"
                    />

                    <p className="leading-relaxed">
                        Personal trainers in Singapore are not government-licensed, so the responsibility to verify credentials sits with you. This guide covers
                        what to check before you book, the warning signs worth walking away from, and how to tell when it is time to change trainers. For what
                        you should expect to pay, see our guide to{' '}
                        <Link href="/sg/personal-trainer/personal-trainer-rates-singapore" prefetch={false} className="text-orange-400 hover:text-orange-300 underline">
                            personal trainer rates in Singapore
                        </Link>.
                    </p>

                    <div className="bg-amber-500/10 p-4 rounded-lg border-l-4 border-amber-500">
                        <p className="text-sm text-white/60">
                            <strong>Before you book:</strong> Ask to see a current certification from a recognised body such as ACE, NASM, NSCA or ISSA, confirm the
                            trainer carries public liability insurance, and get the full price and what it includes in writing before paying. Booking through a
                            platform like StringsSG means credentials are verified for you.
                        </p>
                    </div>

                    {/* Vetting: red flags and avoiding overpaying */}
                    <section className="space-y-6 bg-white/4 backdrop-blur-xl border border-red-400/20 p-6 rounded-2xl shadow-xl shadow-red-500/10">
                        <header className="text-center">
                            <h2 className="text-2xl md:text-3xl font-bold text-red-400 mb-2">
                                Red flags and how not to overpay
                            </h2>
                            <p className="text-white/50 text-sm">Most disputes come down to unclear pricing and unverified credentials. Both are avoidable.</p>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-white/5 py-6 px-4 rounded-lg border border-white/8">
                                <h3 className="text-lg font-bold text-red-400 mb-4">Red flags to avoid</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start">
                                        <span className="text-red-400 mr-2">•</span>
                                        <span className="text-white/60">Rates far below the market (under $50 per session) without a clear reason such as a trainee coach or an off-peak trial.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-red-400 mr-2">•</span>
                                        <span className="text-white/60">No verifiable certification from a recognised body such as ACE, NASM, NSCA or ISSA.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-red-400 mr-2">•</span>
                                        <span className="text-white/60">High-pressure sales tactics or &quot;limited-time&quot; packages you are asked to sign immediately.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-red-400 mr-2">•</span>
                                        <span className="text-white/60">Guaranteed results in unrealistic timeframes.</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-white/5 py-6 px-4 rounded-lg border border-white/8">
                                <h3 className="text-lg font-bold text-green-400 mb-4">How not to overpay</h3>
                                <ul className="space-y-3">
                                    <li className="flex items-start">
                                        <span className="text-green-400 mr-2">•</span>
                                        <span className="text-white/60">Check reviews and ask for case studies or client results relevant to your goal.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-400 mr-2">•</span>
                                        <span className="text-white/60">Ask about package rates and off-peak pricing before committing.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-400 mr-2">•</span>
                                        <span className="text-white/60">Clarify inclusions: travel to your location, equipment, nutrition guidance and support between sessions.</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-400 mr-2">•</span>
                                        <span className="text-white/60">Take a trial session first to assess coaching style and professionalism.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* When to switch */}
                    <section className="bg-white/4 backdrop-blur-xl border border-amber-400/20 rounded-2xl py-6 px-4 shadow-xl shadow-amber-500/10">
                        <h2 className="text-center font-bold text-2xl text-amber-400 mb-4">
                            When to switch personal trainers
                        </h2>
                        <p className="text-white/60 mb-6 text-center text-sm">
                            Staying with a poor fit costs you both money and progress. These are the signals worth acting on.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white/5 p-5 rounded-lg border border-white/8 border-l-4 border-l-red-400">
                                <h3 className="font-bold text-red-400 mb-3">Signs it may be time to change</h3>
                                <ul className="space-y-2 text-sm text-white/60">
                                    <li>• <strong>No progress after 8-12 weeks</strong> despite consistent effort</li>
                                    <li>• <strong>The same workout every session</strong>, with no progression</li>
                                    <li>• <strong>Distracted during your session</strong></li>
                                    <li>• <strong>Frequently late or cancels</strong> without makeup sessions</li>
                                    <li>• <strong>Cannot explain the reasoning</strong> behind exercises</li>
                                    <li>• <strong>Pushes supplements</strong> you did not ask about</li>
                                    <li>• <strong>Ignores feedback</strong> or injury concerns</li>
                                </ul>
                            </div>
                            <div className="bg-white/5 p-5 rounded-lg border border-white/8 border-l-4 border-l-green-400">
                                <h3 className="font-bold text-green-400 mb-3">Signs you are in good hands</h3>
                                <ul className="space-y-2 text-sm text-white/60">
                                    <li>• <strong>Measurable progress</strong> every 4-6 weeks</li>
                                    <li>• <strong>Programmes evolve</strong> as you improve</li>
                                    <li>• <strong>Full attention</strong> during sessions</li>
                                    <li>• <strong>Reliable and punctual</strong></li>
                                    <li>• <strong>Explains form and principles</strong> as you train</li>
                                    <li>• <strong>Honest about nutrition</strong> without selling products</li>
                                    <li>• <strong>Adapts to your feedback</strong> and energy levels</li>
                                </ul>
                            </div>
                        </div>
                        <div className="mt-4 bg-blue-500/10 p-4 rounded-lg border-l-4 border-blue-400">
                            <p className="text-sm text-white/60">
                                <strong>How to switch gracefully:</strong> Complete any sessions you have already paid for, then let the trainer know you are trying a
                                different approach. You do not owe a detailed explanation, and a professional will understand.
                            </p>
                        </div>
                    </section>

                    <ArticleByline author={harveyFinn} variant="footer" />

                    <div className="text-center pt-4">
                        <Link href="/sg/personal-trainer" prefetch={false}
                            className="inline-flex items-center gap-2 px-8 py-4 bg-linear-to-r from-orange-500 to-amber-500 text-white font-bold rounded-full shadow-lg shadow-orange-500/20 hover:opacity-90 transition-all duration-300">
                            Find a Certified Personal Trainer
                        </Link>
                    </div>
                </div>
            </div>
        </>
    )
}
